import * as React from "react";
import { observer } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useRootStore } from "../../store/contexts";
import { graphqlToDateTime } from "../utils";

export const SystemStatusOverlay: React.FC = observer(() => {
  const rootStore = useRootStore();
  const { systemStatusStore } = rootStore;

  const [countdown, setCountdown] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!systemStatusStore.isMaintenanceScheduled) {
      setCountdown(null);
      return;
    }

    const interval = setInterval(() => {
      const seconds = systemStatusStore.secondsUntilMaintenance;
      setCountdown(seconds);
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- interval reads latest secondsUntilMaintenance each tick; including it would re-create the interval every second
  }, [systemStatusStore.isMaintenanceScheduled, systemStatusStore.systemStatus?.scheduledAt]);

  React.useEffect(() => {
    if (systemStatusStore.systemStatus?.status === "operational") {
      systemStatusStore.handleMaintenanceTransition();
    }
  }, [systemStatusStore.systemStatus?.status, systemStatusStore]);

  if (!systemStatusStore.shouldShowOverlay) {
    return null;
  }

  const { systemStatus } = systemStatusStore;
  const isScheduled = systemStatus?.status === "maintenance_scheduled";
  const isMaintenance = systemStatus?.status === "maintenance";

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  return (
    <Dialog
      open={true}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: "fixed",
          top: 20,
        },
      }}
    >
      <DialogTitle>
        {isScheduled && "Mantenimiento Programado"}
        {isMaintenance && "Sistema en Mantenimiento"}
      </DialogTitle>
      <DialogContent>
        {systemStatus?.message && (
          <Alert severity={isScheduled ? "warning" : "info"} sx={{ mb: 2 }}>
            {systemStatus.message}
          </Alert>
        )}

        {isScheduled && countdown !== null && countdown > 0 && (
          <Box sx={{ textAlign: "center", my: 3 }}>
            <Typography variant="h3" color="primary" sx={{ fontWeight: "bold" }}>
              {formatTime(countdown)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Tiempo hasta el mantenimiento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Por favor, guardá tu trabajo. El sistema entrará en mantenimiento
              pronto.
            </Typography>
          </Box>
        )}

        {isMaintenance && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", my: 3 }}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              El sistema está en mantenimiento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Estamos actualizando el sistema. Un momento, por favor.
            </Typography>
          </Box>
        )}

        {systemStatus?.estimatedEnd && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
            Finalización estimada:{" "}
            {graphqlToDateTime(systemStatus.estimatedEnd)}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
});
